import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../plans/plans.scss';
import useMediaQuery from '@mui/material/useMediaQuery';
import bannerImg3 from '../../assets/images/BannerFullBodyMRI.png';
import checkIcon from '../../assets/icons/CheckTick.svg';
import arrowRightIcon from '../../assets/icons/whiteArrow.png';
import { getStripeProducts, StripeProduct } from '../../api/subscription';

const scanPlan = {
  id: 'fullBody' as const,
  title: 'Full-body MRI',
  description: 'A one-time internal snapshot',
  bannerImg: bannerImg3,
  defaultPrice: 1650,
  list: [
    'In-app full-body scan report',
    'Doctor review + consult',
    'Pre and post scan clinician consults',
    'Report organised by body part',
    '60-90 minutes in the scanner'
  ]
};

const PlanHeader: React.FC<{ product: StripeProduct | null }> = ({
  product
}) => {
  const amount = product?.yearly_amount ?? scanPlan.defaultPrice;

  return (
    <div className='plan-card-header d-flex'>
      <div>
        <input
          type='radio'
          name='plan-type'
          className='plan-type-radio'
          checked
          readOnly
        />
        <span className='radio-btn'></span>
      </div>
      <div className='plan-card-header-info'>
        <div>
          {scanPlan.title}
          <p>{scanPlan.description}</p>
        </div>
        <div className='plan-card-header-price'>
          £{amount.toLocaleString()}
          <span>/once-off</span>
        </div>
      </div>
    </div>
  );
};

const OrderSummary: React.FC<{
  product: StripeProduct | null;
}> = ({ product }) => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  const amount = product?.yearly_amount ?? scanPlan.defaultPrice;

  return (
    <div className='plan-info'>
      <div className='order-summary-banner'>
        <img src={scanPlan.bannerImg} alt={`${scanPlan.title} banner`} />
      </div>
      {!isMobile && <div className='order-summary-title'>{scanPlan.title}</div>}
      {!isMobile && (
        <p className='order-summary-description'>{scanPlan.description}</p>
      )}
      <ul className='order-summary-list'>
        {scanPlan.list.map((item, index) => (
          <li key={`${item}-${index}`}>
            <img src={checkIcon} alt='check' />
            {item}
          </li>
        ))}
      </ul>
      <div className='total-price'>
        <div>Total</div>
        <div>
          £{amount.toLocaleString()}
          <span>/once-off</span>
        </div>
      </div>
    </div>
  );
};

const ScanLanding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await getStripeProducts();
        const activeProducts = response.products.filter(
          (product: StripeProduct) => product.active
        );
        setProducts(activeProducts);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const fullBodyProduct =
    products.find((p) => p.key === 'full_body_mri') || null;

  const handleNext = () => {
    // Store selected plan data (same format as other plans)
    const planData = {
      type: 'fullBody' as const,
      testCount: null,
      product: fullBodyProduct,
      productId: fullBodyProduct?.stripe_price_yearly_id || fullBodyProduct?.stripe_price_monthly_id || null,
      paymentType: 'yearly' as const
    };
    localStorage.setItem('selectedPlan', JSON.stringify(planData));
    
    // Navigate to signup/details page (same as other plans)
    const token = searchParams.get('token');
    const query = token ? `?token=${encodeURIComponent(token)}` : '';
    navigate(`/signup/today${query}`);
  };

  return (
    <div className='c_container subscription-page-wrapper'>
      <div className='two-colum-grid'>
        <div className='left-column'>
          <div className='become-member-text'>Book your scan</div>
          <div className='journey-starts-here'>
            Your full-body MRI starts here
          </div>
          <div
            className='d-flex align-items-center plan-term-wrapper'
            style={{ marginBottom: '16px' }}
          >
            <div className='plan-term-check'>
              <span className='btn active'>One-time payment</span>
            </div>
          </div>
          {!loading && (
            <>
              <div className='plan-card-wrapper full-body active'>
                <PlanHeader product={fullBodyProduct} />
              </div>
             
              {isMobile && (
                <div className='inner-card'>
                  <OrderSummary product={fullBodyProduct} />
                </div>
              )}
            </>
          )}
          <div>
            <div>
              <button onClick={handleNext} className='plans-step-btn'>
                Next - your details{' '}
                <img src={arrowRightIcon} alt='arrow right' />
              </button>
            </div>
          </div>
        </div>
        {!isMobile && (
          <div className='right-column'>
            <div className='inner-card'>
              <h4 className='inner-card-title'>Order summary</h4>
              {loading ? (
                <div className='inner-card-info'>
                  <div>Loading pricing...</div>
                </div>
              ) : (
                <OrderSummary product={fullBodyProduct} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanLanding;


